import React, { useState, useEffect } from "react";
import { DatePicker, message, Button, Select } from "antd";
import dayjs from "dayjs";
import { postRequest, putRequest, getRequest } from "../service";

const AddPublicHoliday = ({ onClose, onSuccess, editData }) => {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loading, setLoading] = useState({
    employees: false
  });

  const [formData, setFormData] = useState({
    title: "",
    startDate: null,
    endDate: null,
    days: 0,
    description: "",
    selectedEmployees: [],
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Refetch employees when dates change to update the API call with new date parameters
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      fetchEmployees();
    }
  }, [formData.startDate, formData.endDate]);

  // Prefill form if editing
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        startDate: editData.startDate ? dayjs(editData.startDate) : null,
        endDate: editData.endDate ? dayjs(editData.endDate) : null,
        days: editData.days || 0,
        description: editData.description || "",
        selectedEmployees: editData.employeeIds || [],
      });
    } else {
      setFormData({
        title: "",
        startDate: null,
        endDate: null,
        days: 0,
        description: "",
        selectedEmployees: [],
      });
    }
  }, [editData]);

  // Set default selections when options are loaded (only for new holidays, not edit mode)
  useEffect(() => {
    if (employeeOptions.length > 0 && !editData) {
      setFormData(prev => ({
        ...prev,
        selectedEmployees: employeeOptions.map(opt => opt.value)
      }));
    }
  }, [employeeOptions, editData]);

  // Calculate number of days whenever startDate or endDate changes
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = dayjs(formData.startDate).startOf("day");
      const end = dayjs(formData.endDate).startOf("day");
      const diff = end.diff(start, "day") + 1;
      setFormData((prev) => ({
        ...prev,
        days: diff > 0 ? diff : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        days: 0,
      }));
    }
  }, [formData.startDate, formData.endDate]);

  const fetchEmployees = async () => {
    try {
      setLoading(prev => ({ ...prev, employees: true }));
      
      // Build URL with date parameters if available
      let url = '/api/companies/reportto';
      if (formData.startDate && formData.endDate) {
        const startDate = formData.startDate.format('YYYY-MM-DD');
        const endDate = formData.endDate.format('YYYY-MM-DD');
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
      
      const response = await getRequest(url);
      if (response?.employees) {
        const formattedOptions = response.employees.map(item => ({
          value: item._id,
          label: item.name,
          department: item.department,
          designation: item.designation
        }));
        setEmployeeOptions(formattedOptions);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(prev => ({ ...prev, employees: false }));
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectAll = (type, options) => {
    setFormData(prev => ({
      ...prev,
      [type]: options.map(opt => opt.value)
    }));
  };

  const handleUnselectAll = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: []
    }));
  };

  const getSelectOptions = (options, type) => {
    return [
      {
        label: (
          <div className="flex justify-between items-center">
            <span>Select All</span>
            <span className="text-xs text-gray-500">Select all options</span>
          </div>
        ),
        value: 'select-all',
        className: 'select-all-option'
      },
      {
        label: (
          <div className="flex justify-between items-center">
            <span>Unselect All</span>
            <span className="text-xs text-gray-500">Clear all selections</span>
          </div>
        ),
        value: 'unselect-all',
        className: 'unselect-all-option'
      },
      {
        type: 'divider'
      },
      ...options
    ];
  };

  const handleSelectChange = (type, value, options) => {
    if (value.includes('select-all')) {
      handleSelectAll(type, options);
    } else if (value.includes('unselect-all')) {
      handleUnselectAll(type);
    } else {
      handleChange(type, value.filter(v => v !== 'select-all' && v !== 'unselect-all'));
    }
  };

  // Custom filter function for searching employees
  const filterOption = (input, option) => {
    // Skip filtering for Select All and Unselect All options
    if (option.value === 'select-all' || option.value === 'unselect-all') {
      return true;
    }
    
    // Skip filtering for divider
    if (option.type === 'divider') {
      return true;
    }
    
    const searchText = input.toLowerCase();
    
    // Check if option.label is a string before calling toLowerCase
    const name = typeof option.label === 'string' ? option.label.toLowerCase() : '';
    const department = typeof option.department === 'string' ? option.department.toLowerCase() : '';
    const designation = typeof option.designation === 'string' ? option.designation.toLowerCase() : '';
    
    return name.includes(searchText) || 
           department.includes(searchText) || 
           designation.includes(searchText);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.startDate || !formData.endDate) {
      message.error("Please fill all required fields.");
      return;
    }
    if (formData.days <= 0) {
      message.error("End date must be after start date.");
      return;
    }
    try {
      const payload = {
        title: formData.title,
        startDate: dayjs(formData.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(formData.endDate).format("YYYY-MM-DD"),
        days: formData.days,
        employeeIds: formData.selectedEmployees,
        description: formData.description,
      };
      let response;
      if (editData && editData._id) {
        // Edit mode
        response = await putRequest(`/api/publicholidays?holidayId=${editData._id}`, payload);
        if (response.message === "Holiday updated successfully") {
          message.success(response.message || "Public holiday updated successfully");
          if (onSuccess) onSuccess();
        } else {
          message.error(response?.error || "Failed to update public holiday");
        }
      } else {
        // Add mode
        response = await postRequest("/api/publicholidays", payload);
        if (response.message == "Public holiday created successfully") {
          message.success(response.message || "Public holiday created successfully");
          if (onSuccess) onSuccess();
        } else {
          message.error(response?.error || "Failed to create public holiday");
        }
      }
    } catch (error) {
      message.error("An error occurred while saving.");
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-1">
        <label className="text-md">
          Title hhh
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
          placeholder="Enter holiday title"
        />
      </div>

      {/* Dates and Number of Days in one line, responsive */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
        {/* Start Date */}
        <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <label className="text-md">
            Start Date
          </label>
          <DatePicker
            value={formData.startDate}
            onChange={(date) => handleChange("startDate", date)}
            className="w-full"
            format="YYYY-MM-DD"
            placeholder="Select start date"
          />
        </div>

        {/* End Date */}
        <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <label className="text-md">
            End Date
          </label>
          <DatePicker
            value={formData.endDate}
            onChange={(date) => handleChange("endDate", date)}
            className="w-full"
            format="YYYY-MM-DD"
            placeholder="Select end date"
            disabledDate={(current) =>
              formData.startDate ? current && current < dayjs(formData.startDate).startOf("day") : false
            }
          />
        </div>

        {/* Number of Days */}
        <div className="flex flex-col gap-1 flex-1 min-w-[100px]">
          <label className="text-md whitespace-nowrap">No. of Days</label>
          <input
            type="number"
            value={formData.days}
            readOnly
            className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md bg-gray-100 w-full"
          />
        </div>
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1">
        <label className="text-md">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
          placeholder="Enter description"
          rows={3}
        />
      </div>

      {/* Employee Selection */}
      <div className="flex flex-col gap-1">
        <label className="text-md">Select Employees</label>
        <Select
          mode="multiple"
          value={formData.selectedEmployees}
          onChange={(value) => handleSelectChange("selectedEmployees", value, employeeOptions)}
          options={getSelectOptions(employeeOptions, "selectedEmployees")}
          className="w-full"
          placeholder="Select employees"
          loading={loading.employees}
          maxTagCount={3}
          maxTagTextLength={20}
          showSearch={true}
          filterOption={filterOption}
          optionFilterProp="label"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          className="bg-primary hover:bg-primary/80"
        >
          {editData ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default AddPublicHoliday;
